import { gql } from '@apollo/client';
import type { AbDimensionCandidate } from './getAbDimensionCandidates';
import type { AbRetirePreview } from './previewAbRetire';

/**
 * 「站点作为 A」的 AB 映射：维度候选 + 退役预览（2.2 需求点2）。
 *
 * <p><b>⚠️ 与站点表单里既有的「错误地址映射」方向相反</b>：
 * 那组（`getStationAbMappings` / `saveStationAbMappings`）是「错误地址 → 当前站点」，当前站点当 **B**；
 * 本文件这组是「当前站点 → 目标站点」，当前站点当 **A**、整站搬走。改之前先确认自己在改哪一组。
 *
 * <p>响应结构与审核池那条路**完全一致**（复用 `AbDimensionCandidate` / `AbRetirePreview`），
 * 因为后端就是同一套实现，只是 A 侧来源不同 —— 对应 PRD「交互逻辑：同现有的存量站点的AB映射」。
 */
export const GET_STATION_AB_DIMENSION_CANDIDATES = gql`
  query GetStationAbDimensionCandidates($cooperationWarehouseId: Long!) {
    getStationAbDimensionCandidates(cooperationWarehouseId: $cooperationWarehouseId) {
      contactName
      contactPhone
      channelId
      mappedCooperationWarehouseId
    }
  }
`;

export interface GetStationAbDimensionCandidatesResponse {
  getStationAbDimensionCandidates?: AbDimensionCandidate[] | null;
}

/**
 * 「站点作为 A」的整站作废预览。两个调用时机与审核池那条路一致：
 * ① 点「AB地址映射」按钮时传 `targetBCooperationWarehouseId: null` + `fullySelected: true`
 *    （「假设全选会怎样」），把拦截理由提前摊开；
 * ② 提交前传真实 B 与勾选状态。
 *
 * <p>比审核池那条多两个字段：`existingAbSourceMappingCount` 与已指向的 B 列表 ——
 * 站点入口没有审核池条目那样的状态位判重，两个运营并发映射会静默互覆盖，入口据此提示。
 */
export const PREVIEW_STATION_AB_RETIRE = gql`
  query PreviewStationAbRetire(
    $cooperationWarehouseId: Long!
    $targetBCooperationWarehouseId: Long
    $fullySelected: Boolean!
  ) {
    previewStationAbRetire(
      cooperationWarehouseId: $cooperationWarehouseId
      targetBCooperationWarehouseId: $targetBCooperationWarehouseId
      fullySelected: $fullySelected
    ) {
      applicable
      willRetire
      blockedReason
      cooperationWarehouseId
      cooperationWarehouseName
      addressCount
      cityCount
      contactCount
      channelCount
      estimatedRows
      maxCityCount
      maxAddressCount
      maxContactCount
      maxChannelCount
      maxEstimatedRows
      blockingAppointments {
        appointmentNumber
        expectedPickupDate
        status
      }
      existingAbSourceMappingCount
      existingAbTargetCooperationWarehouseIds
    }
  }
`;

export interface StationAbRetirePreview extends AbRetirePreview {
  /**
   * 本站点当前已存在的「A 侧」active 映射数。
   *
   * <p>非 0 表示**本站已经被别人映射走过**。站点入口没有审核池条目那样的
   * `process_status` 判重，两个运营先后打开同一站点各自映射到不同的 B，
   * 后提交的会让先提交的失效、而双方都看到"保存成功"。
   *
   * <p>⚠️ 这是**提示不是锁** —— 提示到提交之间仍有窗口。
   */
  existingAbSourceMappingCount: number;
  /** 上面那些映射当前指向的 B 站点 id（去重）。 */
  existingAbTargetCooperationWarehouseIds: number[];
}

export interface PreviewStationAbRetireData {
  previewStationAbRetire: StationAbRetirePreview;
}
